import '@forest/native-diagnostics'

import {
  getProducts,
  getReceiptSystemAccountIdSafe,
  initializeNativeBridge,
  native,
  pageVisibleOnce,
  withPageLoadingRecovery,
} from '@forest/native-services'
import { getRemoteStates } from '~/lib/get-remote-states'
import { getUserInfoWithRecovery } from '~/lib/get-user-info-with-recovery'
import { productsReady } from '~/lib/message'
import { appVersion, language, platform } from '~/lib/params'
import { overwriteGetLocale, type Locale } from '~/paraglide/runtime'
import { api } from '~/services/api'
import {
  $isTrialEligible,
  $receiptSystemAccountId,
  userInfoSource,
} from '~/store'
import './lib/posthog'

overwriteGetLocale(() => language as Locale)

initializeNativeBridge({
  bifrost: { enableDebug: import.meta.env.MODE !== 'production' },
  onPageVisible: () => pageVisibleOnce.done(),
})

const userInfoPromise = getUserInfoWithRecovery(userInfoSource)

// Kick off getProducts as early as possible; it starts native prefetches immediately.
const productsPromise = withPageLoadingRecovery(() =>
  getProducts({
    cacheKeyPrefix: 'newUserPaywall.products.exp.v2',
    api,
    appVersion,
    platform,
    deferredUserInfo: userInfoPromise,
    env: import.meta.env.MODE as 'production' | 'staging' | 'dev',
    shouldMock: import.meta.env.DEV,
  }),
)

if (import.meta.env.DEV) {
  pageVisibleOnce.done()
}

userInfoPromise
  .then(async (userInfo) => {
    void getReceiptSystemAccountIdSafe(appVersion).then((id) => {
      $receiptSystemAccountId.set(id)
    })

    // Each prerequisite recovers on its own so a retry never discards the
    // other's completed work, and the continuation below still runs once.
    const [products, { chinaAndroidTrialEligibility }] = await Promise.all([
      productsPromise,
      withPageLoadingRecovery(() => getRemoteStates(userInfo.user_id)),
    ])

    const productIds = [
      products.annualOriginal.product_id,
      products.annualEarlyBird.product_id,
      products.monthlyOriginal.product_id,
      products.monthlyEarlyBird.product_id,
    ]
      .sort((a, b) => a - b)
      .join(',')
    native.general.logEvent({
      event_name: 'plus_price_test_config',
      event_params: { product_ids: productIds },
    })

    const isTrialEligible =
      (products.annualEarlyBird.eligible_trial_days ?? 1) > 0
    $isTrialEligible.set(isTrialEligible)

    productsReady.done(products)

    if (isTrialEligible || chinaAndroidTrialEligibility === 'available') {
      ready('new_user_paywall_trial')
    } else {
      ready('new_user_paywall_subscribe')
    }
  })
  .catch(() => {
    console.error('Failed to get user info or remote states')
  })

function ready(pageName: string) {
  native.general.logEvent({
    event_name: 'page_view',
    event_params: { page_name: pageName },
  })
  native.general.pageReady()
}
